import { OrganizationModel } from "../../models/organization/organization.model";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { Injectable } from "@angular/core";
import { CompoApp } from "../../services/CompoApp";
import { EMPTY, finalize, from, Observable, of, throwError } from "rxjs";
import { delay, map } from "rxjs/operators";
import { RequestClientService } from "../../services/client/request-client.service";
import { OrganizationDetailAction } from "./organization-detail.action";
import { OrganizationRequestService } from "../../services/request/organization-request.service";
import { ResetStateDefaults } from "../base-action-class.model";

interface OrganizationDetailStateModel {
  id: string;
  organization: OrganizationModel;
  inProgress: boolean;
}

const getDefaults = (): OrganizationDetailStateModel => ({
  id: null,
  organization: null,
  inProgress: false
});

@State<OrganizationDetailStateModel>({
  name: 'organizationDetail',
  defaults: getDefaults()
})
@Injectable()
export class OrganizationDetailXsState {

  //region CONSTRUCTOR
  constructor(private app: CompoApp,
              private organizationRequestService: OrganizationRequestService) {
  }

  //endregion

  //region SELECTOR
  @Selector()
  static organizationId(state: OrganizationDetailStateModel): string {
    return state.id || null;
  }

  @Selector()
  static organization(state: OrganizationDetailStateModel): OrganizationModel | null {
    return state.organization || null;
  }

  @Selector()
  static inProgress(state: OrganizationDetailStateModel): boolean {
    return state.inProgress;
  }

  @Selector()
  static organizationLogoId(state: OrganizationDetailStateModel): string | null {
    return state.organization?.organizationLogoId;
  }
  //endregion

  //region ACTIONS
  @Action(OrganizationDetailAction.LoadOrganization)
  public loadOrganization(ctx: StateContext<OrganizationDetailStateModel>, action: OrganizationDetailAction.LoadOrganization): Observable<void> {
    const state = ctx.getState();
    if (state.inProgress) {
      return throwError(() => new Error('Organization request is in progress!'));
    }
    if (!action.id) {
      return throwError(() => new Error('Organization id must be set!'));
    }
    if (state.id === action.id) {
      return of(null);
    }

    ctx.patchState({
      id: null,
      organization: null
    });

    return from(
      this.organizationRequestService.get(action.id, this.app.data.getAccessMode()))
      .pipe(
        delay(RequestClientService.DEFAULT_TIMEOUT),
        map((organization: OrganizationModel) => {
          ctx.patchState({
            id: organization.id,
            organization: organization,
          });
        })
      );
  }

  @Action(OrganizationDetailAction.Update)
  public update(
    ctx: StateContext<OrganizationDetailStateModel>,
    action: OrganizationDetailAction.Update
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Organization request is in progress!'));
    }
    if (!action.model) {
      return throwError(() => new Error('Basic info must be set!'));
    }

    ctx.patchState({
      inProgress: true
    });

    return from(this.organizationRequestService.update(action.model))
      .pipe(
        map((organization) => {
          ctx.patchState({organization});
        }),
        finalize(() => {
          ctx.patchState({inProgress: false});
        })
      );
  }

  @Action(OrganizationDetailAction.RefreshOrganizationLogo)
  public deleteOrganizationLogo(ctx: StateContext<OrganizationDetailStateModel>,
                                action: OrganizationDetailAction.RefreshOrganizationLogo): Observable<void> {
    const state = ctx.getState();

    const orgNewState = {
      ...state.organization,
      organizationLogoId: action.orgLogoId
    };

    ctx.patchState({
      organization: orgNewState
    });

    return EMPTY;
  }

  @Action(OrganizationDetailAction.MakeServiceProviderGloballyVisible)
  public makeServiceProviderGloballyVisible(
    ctx: StateContext<OrganizationDetailStateModel>
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Organization request is in progress!'));
    }
    if (!state.organization?.serviceProviderId) {
      return throwError(() => new Error('Service provider id must be set!'));
    }

    ctx.patchState({
      inProgress: true
    });

    return from(this.organizationRequestService.makeServiceProviderGloballyVisible(state.organization.serviceProviderId))
      .pipe(
        map((organization) => {
          ctx.patchState({organization});
        }),
        finalize(() => {
          ctx.patchState({inProgress: false});
        })
      );
  }

  @Action(OrganizationDetailAction.Delete)
  public delete(
    ctx: StateContext<OrganizationDetailStateModel>,
    action: OrganizationDetailAction.Delete
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Organization request is in progress!'));
    }

    ctx.patchState({
      inProgress: true
    });

    return from(this.organizationRequestService.delete(state.id))
      .pipe(
        map(() => {
          ctx.patchState({organization: null});
        }),
        finalize(() => {
          ctx.patchState({inProgress: false});
        })
      );
  }

  @Action([OrganizationDetailAction.ResetState, ResetStateDefaults])
  public resetState(ctx: StateContext<OrganizationDetailStateModel>): void {
    ctx.patchState({...getDefaults()});
  }

  //endregion

}
